/**
 * The command bar's app-wide plumbing (docs/build/Fyr_command_bar_UX_spec.md §5.2, §10):
 *   • "Show button labels" — a per-ACCOUNT preference (decision 2, 27 Oct 2026): stored with the person on the server,
 *     so it follows them to every device; re-read when the window regains focus, so a change made elsewhere arrives.
 *   • the Cmd/Ctrl+K command palette — what the current object's command bar offers (already RBAC-filtered: the bar
 *     only holds commands the person may run) plus navigation. The palette itself is its own chunk, loaded on first use.
 */
import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_PERSON_PREFS, type PersonPrefsDto } from '@fyr/shared';
import { api, type MeResponse } from './api';
import { loadExtra } from './i18n';

// ── Show button labels (per account) ───────────────────────────────────────────────────────────────────────────────
const PREFS_KEY = ['person-prefs'];

/** [labels on?, set it] — the person's own choice, the same on every device they use. */
export function useButtonLabels(): [boolean, (on: boolean) => Promise<void>] {
  const qc = useQueryClient();
  const me = qc.getQueryData<MeResponse>(['me']);
  const q = useQuery({
    queryKey: PREFS_KEY,
    queryFn: () => api.get<PersonPrefsDto>('/auth/prefs'),
    // `me` already carries them, so the first paint is right; a change made on another device arrives on focus
    initialData: me?.prefs,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const on = q.data?.showButtonLabels ?? DEFAULT_PERSON_PREFS.showButtonLabels;
  const set = useCallback(
    async (next: boolean) => {
      const before = qc.getQueryData<PersonPrefsDto>(PREFS_KEY);
      // optimistic: every bar changes at once (only widths change — nothing moves)
      qc.setQueryData<PersonPrefsDto>(PREFS_KEY, {
        ...DEFAULT_PERSON_PREFS,
        ...before,
        showButtonLabels: next,
      });
      try {
        const saved = await api.put<PersonPrefsDto>('/auth/prefs', { showButtonLabels: next });
        qc.setQueryData(PREFS_KEY, saved);
        qc.setQueryData<MeResponse>(['me'], (m) => m && { ...m, prefs: saved });
      } catch (e) {
        qc.setQueryData(PREFS_KEY, before);
        throw e;
      }
    },
    [qc],
  );
  return [on, set];
}

// ── Command palette (Cmd/Ctrl+K) ─────────────────────────────────────────────────────────────────────────────────────
export interface PaletteEntry {
  id: string;
  label: string;
  /** Where it comes from: the bar's name ("Document actions"), or a folder path for a place. */
  context?: string;
  icon?: ReactNode;
  /** Extra words it is found by. */
  keywords?: string;
  run: () => void;
}
export type PaletteSection = 'object' | 'nav';

interface Registry {
  set: (owner: symbol, section: PaletteSection, entries: PaletteEntry[] | null) => void;
  read: () => Record<PaletteSection, PaletteEntry[]>;
}
const RegistryCtx = createContext<Registry | null>(null);

/** Offer these entries in the palette while the calling component is on screen. */
export function usePaletteEntries(section: PaletteSection, entries: PaletteEntry[]) {
  const reg = useContext(RegistryCtx);
  const owner = useRef(Symbol(section)).current;
  // entries carry fresh closures every render: keep the latest without re-registering on each one
  const latest = useRef(entries);
  latest.current = entries;
  useEffect(() => {
    if (!reg) return;
    reg.set(owner, section, latest.current);
  });
  useEffect(() => () => reg?.set(owner, section, null), [reg, owner, section]);
}

// The palette and its strings load the first time someone presses Cmd/Ctrl+K.
const CommandPalette = lazy(() =>
  Promise.all([import('./components/CommandPalette'), loadExtra()]).then(([m]) => ({
    default: m.CommandPalette,
  })),
);

/** Holds what the palette can offer and opens it on Cmd/Ctrl+K (only for someone signed in with an organisation). */
export function CommandPaletteProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const store = useRef(new Map<symbol, { section: PaletteSection; entries: PaletteEntry[] }>());
  const registry = useMemo<Registry>(
    () => ({
      set: (owner, section, entries) => {
        if (entries) store.current.set(owner, { section, entries });
        else store.current.delete(owner);
      },
      read: () => {
        const out: Record<PaletteSection, PaletteEntry[]> = { object: [], nav: [] };
        for (const { section, entries } of store.current.values()) out[section].push(...entries);
        return out;
      },
    }),
    [],
  );
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey || e.key.toLowerCase() !== 'k') return;
      e.preventDefault();
      setOpen((o) => !o);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [enabled]);
  return (
    <RegistryCtx.Provider value={registry}>
      {children}
      {open && enabled && (
        <Suspense fallback={null}>
          <CommandPalette entries={registry.read()} onClose={() => setOpen(false)} />
        </Suspense>
      )}
    </RegistryCtx.Provider>
  );
}
